import { Typography } from '@mui/material';
import { PropsWithChildren, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

type GroupMeta<TGroup extends string> = {
  key: TGroup;
  title: string;
  order?: number; // optional if you don’t always need it
};

type InputGroupedProps<TOption, TGroup extends string> = {
  name: string;
  options: TOption[];
  getGroup: (option: TOption) => GroupMeta<TGroup>;
  renderInput: (props: { name: string; options: TOption[] }) => React.ReactNode;
};

export const InputGrouped = <TOption, TGroup extends string>(
  props: InputGroupedProps<TOption, TGroup>
) => {

  const groupedEntries = useMemo(() => {
    const groupMap = new Map<string, GroupMeta<TGroup> & { options: TOption[] }>();

    for (const option of props.options) {
      const groupMeta = props.getGroup(option);

      if (!groupMap.has(groupMeta.key)) {
        groupMap.set(groupMeta.key, {
          key: groupMeta.key,
          title: groupMeta.title,
          order: groupMeta.order,
          options: [],
        });
      }

      groupMap.get(groupMeta.key).options.push(option);
    }

    return Array.from(groupMap.values()).sort((a, b) => {
      const aOrder = a.order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.order ?? Number.MAX_SAFE_INTEGER;

      return aOrder - bOrder;
    });
  }, [ props ]);

  return (
    <div>
      {groupedEntries.map(({ key, title, options }) => (
        <InputGroupedSection key={key}>
          {title && <InputGroupedSectionTitle>{title}</InputGroupedSectionTitle>}
          {props.renderInput({ name: props.name, options })}
        </InputGroupedSection>
      ))}
    </div>
  );
};

const useStyles = makeStyles()(theme => ({
  checkboxSectionLabel: {
    marginBottom: theme.spacing(1),
    display: 'block',
    color: theme.palette.text.secondary,
  },
  section: {
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    padding: theme.spacing(2),
  }
}));

export const InputGroupedSection = (props: PropsWithChildren<object>) => {
  const { classes } = useStyles();

  return (
    <div className={classes.section}>
      {props.children}
    </div>
  );
};

export const InputGroupedSectionTitle = (props: PropsWithChildren<object>) => {
  const { classes } = useStyles();

  return (
    <Typography variant="overline" className={classes.checkboxSectionLabel}>
      {props.children}
    </Typography>
  );
};
